import React from 'react';
import clsx from 'clsx';
import type { DetailSurfaceDefinition } from '../surface/defineDetailSurface';

export type SkeletonVariant =
  // Existing shapes
  | 'table'
  | 'card'
  | 'list'
  | 'kpi'
  | 'weekly'
  | 'detail'
  | 'form'
  | 'grid'
  // Surface + content shapes
  | 'pane'
  | 'canvas'
  | 'document'
  | 'prose'
  | 'chat'
  | 'dashboard'
  | 'option';

/**
 * How far the placeholder is held off its container's edges.
 *
 * `auto` - the default - is the gutter the LOADED content has (see
 * `VARIANT_INSET`), so the swap does not shift anything. The named steps are
 * the escape hatch for a caller whose container pads differently.
 */
export type SkeletonInset = 'auto' | 'none' | 'sm' | 'md' | 'lg';

const INSET_CLASS: Record<Exclude<SkeletonInset, 'auto'>, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

/**
 * The gutter each shape's real counterpart sits in - the whole point of
 * `inset="auto"`. A skeleton that hugs the window while the page it stands in
 * for is inset by 12px reads as a jump the moment the data lands, and every
 * page that noticed used to patch it with a `className="p-4"` or a wrapper
 * `<div className="px-4 py-5">` of its own, so no two agreed.
 *
 * An empty string means the loaded content really is flush - either it is
 * full-bleed (a `Table`), or it lives in a slot the caller already pads (a card
 * body, a pane, the caller's own grid).
 */
const VARIANT_INSET: Record<SkeletonVariant, string> = {
  // Full-bleed by nature: `Table` runs edge to edge, and `Table` itself draws
  // this shape inside a `<td className="p-0">`.
  table: '',
  // Always dropped into the caller's own padded grid container.
  grid: '',
  // Slot fillers - the card body / pane / row the caller already pads.
  card: '',
  list: '',
  option: '',
  prose: '',
  chat: '',
  kpi: '',
  weekly: '',
  // `PaneFields` carries its own p-4.
  pane: '',
  // `DocumentScroller` (and every other sheet host) pads around the page.
  document: '',
  // DOCK_INSET - the gutter `DetailPageSurface` puts around canvas and docks.
  detail: 'p-3',
  canvas: 'p-3',
  // `DashboardGrid`'s root padding.
  dashboard: 'p-4 lg:p-6',
  // `FormShell`'s body padding.
  form: 'px-6 py-6',
};

interface SkeletonProps {
  /** Shape of the placeholder. Default "card". */
  variant?: SkeletonVariant;
  /** Number of repeated rows for "table" / "list" / "prose" / "chat" / "option". Default 5. */
  rows?: number;
  /**
   * Number of cells for "kpi".
   *
   * Omitted, a `kpi` skeleton is ONE bordered tile - the shape a caller
   * repeats in its own grid. Given, it is the joined strip instead: one card
   * with N hairline-divided cells, which is what a `GlassCard padding="none"`
   * KPI strip actually looks like, so the placeholder does not snap from four
   * boxes into one card when the data lands.
   */
  columns?: number;
  /**
   * The page's surface definition, for the shapes that mirror
   * `DetailPageSurface`.
   *
   * `detail` reads it to lay out the real docks - which panes open on a first
   * visit, which side each is on, and how wide - so the placeholder has the
   * same silhouette as the page that replaces it. Without it, `detail` falls
   * back to the common arrangement (one left rail pane, canvas, one right
   * pane). `pane` and `canvas` draw a single region and accept the prop for
   * symmetry; they do not need it.
   */
  surface?: DetailSurfaceDefinition<any, any>;
  /**
   * Spacing between the placeholder and its container's edges. Default
   * `auto` - the gutter the loaded content has. Pass `none` when the container
   * already pads (or is deliberately full-bleed).
   */
  inset?: SkeletonInset;
  className?: string;
}

/** The widest step of a KPI strip, spelled out so Tailwind can see the class. */
const KPI_STRIP_COLS: Record<number, string> = {
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
};

/** A single shimmering bar. */
const Bar: React.FC<{ className?: string }> = ({ className }) => (
  <div className={clsx('bg-surface-sunken rounded', className)} />
);

/**
 * A bar in the FIELD fill - the silhouette of an input. `bg-field` is what the
 * real fields wear, so the bar is grey on a glass card and white on the page
 * canvas (`.surface-canvas`), where a surface-sunken bar would vanish into the
 * paper ground.
 */
const FieldBar: React.FC<{ className?: string }> = ({ className }) => (
  <div className={clsx('bg-field rounded border border-hairline-soft', className)} />
);

/** A glass block - the placeholder for a card, a pane or the canvas. */
const Block: React.FC<{ className?: string; children?: React.ReactNode }> = ({ className, children }) => (
  <div className={clsx('glass rounded-lg', className)}>{children}</div>
);

/** A pane's header strip: icon disc + title, matching `Panel`'s GlassCard header. */
const PaneHeader: React.FC = () => (
  <div className="flex items-center gap-2 px-4 py-3 border-b border-hairline-soft">
    <div className="h-4 w-4 rounded bg-surface-sunken flex-shrink-0" />
    <Bar className="h-3 w-24" />
  </div>
);

/** The stacked label/value rows that fill most pane bodies. */
const PaneFields: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <div className="p-4 space-y-4">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="space-y-1.5">
        <Bar className="h-2 w-16" />
        <Bar className="h-3 w-2/3" />
      </div>
    ))}
  </div>
);

/**
 * Standard loading placeholder (#2331). Use on tab/section transitions instead
 * of a centered spinner, a bare "Loading…", or a blank panel. Animated with the
 * app's `animate-fadePulse` convention.
 *
 *   <Skeleton variant="table" rows={6} />
 *   <Skeleton variant="detail" surface={MATTER_SURFACE} />
 *
 * Every shape is drawn in semantic tokens (`bg-surface*`, `border-hairline*`,
 * `.glass`) so it flips with the theme without a single `dark:` utility, and
 * every shape carries the gutter its loaded counterpart has (`VARIANT_INSET`)
 * so the swap doesn't shift the page. Don't wrap a Skeleton in a padded div —
 * pass `inset` instead:
 *
 *   <Skeleton variant="list" rows={8} inset="sm" />
 */
const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'card',
  rows = 5,
  columns,
  surface,
  inset = 'auto',
  className,
}) => {
  const lines = Array.from({ length: rows });
  // Weekly grid: a "Collaborator" column + 7 days + a total column.
  const weekCells = Array.from({ length: 8 });

  /**
   * The docked panes to draw for `detail`, read from the surface's defaults
   * (the arrangement a first visit gets) and ordered exactly as the shell
   * stacks them.
   */
  const paneSlots = (dock: 'left' | 'right'): Array<{ id: string; width: number }> => {
    if (!surface) {
      // No surface declared - the common arrangement.
      return dock === 'left' ? [{ id: 'context', width: 300 }] : [{ id: 'detail', width: 420 }];
    }
    return Object.values(surface.defaults.slots as Record<string, any>)
      .filter((slot: any) => slot.visible && slot.dock === dock)
      .sort((a: any, b: any) => a.index - b.index)
      .map((slot: any) => ({ id: String(slot.id), width: Number(slot.width) || 320 }));
  };

  return (
    <div
      className={clsx(
        'animate-fadePulse w-full',
        inset === 'auto' ? VARIANT_INSET[variant] : INSET_CLASS[inset],
        // A card grid dropped inside the caller's grid would otherwise squeeze
        // its whole three-column mock into one cell. Inert outside a grid.
        variant === 'grid' && 'col-span-full',
        className
      )}
      aria-busy="true"
      aria-live="polite"
      data-testid="skeleton"
    >
      {variant === 'table' && (
        /* THE table-list loader - full-width alternating bands
           (the Matters/Templates/Data Sources pattern). */
        <div className="w-full">
          {lines.map((_, i) => (
            <div
              key={i}
              className={clsx(
                'flex items-center h-16 border-b border-hairline-soft',
                i % 2 === 0 ? 'bg-surface' : 'bg-surface-soft'
              )}
            />
          ))}
        </div>
      )}

      {variant === 'grid' && (
        /* Card-grid loader (grid view of list pages) */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {lines.map((_, i) => (
            <Block key={i} className="p-6 shadow-sm" >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 bg-surface-sunken rounded-full" />
                  <Bar className="h-4 w-32" />
                </div>
                <Bar className="h-4 w-16" />
              </div>
              <div className="mt-4 flex flex-col gap-2">
                <Bar className="h-3 w-1/2" />
                <Bar className="h-3 w-1/3" />
              </div>
            </Block>
          ))}
        </div>
      )}

      {/* A glass card like the one it loads into - never a grey bordered box. */}
      {variant === 'card' && (
        <Block className="p-4 space-y-3">
          <Bar className="h-4 w-1/3" />
          {lines.map((_, i) => (
            <Bar key={i} className={clsx('h-3', i === rows - 1 ? 'w-5/6' : 'w-full')} />
          ))}
        </Block>
      )}

      {variant === 'list' && (
        <div className="space-y-3">
          {lines.map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-surface-sunken flex-shrink-0" />
              <Bar className="h-3 flex-1" />
            </div>
          ))}
        </div>
      )}

      {variant === 'weekly' && (
        <div className="border border-hairline rounded-lg overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-4 px-3 py-3 bg-surface-soft">
            {weekCells.map((_, i) => (
              <Bar key={i} className={clsx('h-2.5', i === 0 ? 'w-28' : 'flex-1')} />
            ))}
          </div>
          {/* Rows */}
          {lines.map((_, r) => (
            <div key={r} className="flex items-center gap-4 px-3 py-3.5 border-t border-hairline-soft">
              <div className="flex items-center gap-2 w-28">
                <div className="h-6 w-6 rounded-full bg-surface-sunken flex-shrink-0" />
                <Bar className="h-3 flex-1" />
              </div>
              {weekCells.slice(1).map((_, c) => (
                <Bar key={c} className="h-3 flex-1" />
              ))}
            </div>
          ))}
        </div>
      )}

      {/*
        Detail page: THE canvas-and-panes silhouette.

        This used to draw a 2/3 + 1/3 grid, which is the layout detail pages
        had BEFORE `DetailPageSurface` - so the placeholder promised one shape
        and the page delivered another, and the swap read as a jump. It now
        draws what the shell draws: glass panes floating at the edges at their
        real docked widths, with the canvas between them.
      */}
      {variant === 'detail' && (
        <div className="flex gap-4 w-full min-h-[24rem]">
          {paneSlots('left').map((slot) => (
            <Block
              key={slot.id}
              className="hidden lg:flex flex-col flex-shrink-0 overflow-hidden"
            >
              <div style={{ width: slot.width }} className="max-w-[40vw]">
                <PaneHeader />
                <PaneFields />
              </div>
            </Block>
          ))}

          {/* The canvas - the page's own content, between the docks. */}
          <div className="flex-1 min-w-0 space-y-4">
            <Block className="p-5 space-y-3">
              <Bar className="h-3 w-24" />
              <Bar className="h-3 w-full" />
              <Bar className="h-3 w-11/12" />
              <Bar className="h-3 w-4/5" />
            </Block>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[0, 1, 2].map((i) => (
                <Block key={i} className="p-4 space-y-2">
                  <Bar className="h-2.5 w-16" />
                  <Bar className="h-3.5 w-24" />
                </Block>
              ))}
            </div>
            <Block className="p-5 space-y-3">
              <Bar className="h-3 w-32" />
              {lines.slice(0, 3).map((_, i) => (
                <Bar key={i} className="h-3 w-full" />
              ))}
            </Block>
          </div>

          {paneSlots('right').map((slot) => (
            <Block key={slot.id} className="hidden xl:flex flex-col flex-shrink-0 overflow-hidden">
              <div style={{ width: slot.width }} className="max-w-[40vw]">
                <PaneHeader />
                <PaneFields count={5} />
              </div>
            </Block>
          ))}
        </div>
      )}

      {/* A pane BODY - what `Panel`/`PaneBody` show while their content loads.
          No header: the panel already drew one. */}
      {variant === 'pane' && (
        <div className="h-full">
          <PaneFields count={Math.max(2, Math.min(rows, 8))} />
          <div className="px-4 pb-4 space-y-2">
            <Bar className="h-16 w-full rounded-lg" />
            <Bar className="h-16 w-full rounded-lg" />
          </div>
        </div>
      )}

      {/* The canvas region on a detail surface, standing alone. */}
      {variant === 'canvas' && (
        <div className="w-full space-y-4">
          <Block className="p-5 space-y-3">
            <Bar className="h-4 w-1/3" />
            <Bar className="h-3 w-full" />
            <Bar className="h-3 w-10/12" />
          </Block>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[0, 1].map((i) => (
              <Block key={i} className="p-5 space-y-3">
                <Bar className="h-3 w-24" />
                <Bar className="h-3 w-full" />
                <Bar className="h-3 w-3/4" />
              </Block>
            ))}
          </div>
        </div>
      )}

      {/*
        A rendered page: a title block, then dense text, ON THE PAPER.

        NOT in a `Block`. Every other variant stands in for cards, so a glass
        card is the right placeholder shape for them; this one stands in for a
        DOCUMENT, and the surface it loads into is already the paper - white,
        full-bleed, edge to edge. Wrapping it drew a translucent card floating
        on that paper, which is the "boxed in" look, and it jumped when the
        real text replaced it, because the text has no card.

        The COLUMN only - no gutters of its own. Both callers already sit in a
        page container that supplies the padding (`DOC_PAGE` in drafting), and
        adding it again here indented the placeholder further than the text it
        stands in for.
      */}
      {variant === 'document' && (
        <div className="w-full flex justify-center">
          <div className="w-full max-w-[860px] space-y-6">
            <div className="space-y-2">
              <Bar className="h-5 w-2/3" />
              <Bar className="h-2.5 w-1/3" />
            </div>
            <div className="space-y-2.5">
              {Array.from({ length: 8 }).map((_, i) => (
                <Bar key={i} className={clsx('h-3', i % 4 === 3 ? 'w-3/5' : 'w-full')} />
              ))}
            </div>
            <div className="space-y-2.5">
              {Array.from({ length: 6 }).map((_, i) => (
                <Bar key={i} className={clsx('h-3', i % 3 === 2 ? 'w-2/3' : 'w-full')} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Streaming/markdown answer text - no chrome, just paragraph lines. */}
      {variant === 'prose' && (
        <div className="space-y-2.5 w-full">
          {lines.map((_, i) => (
            <Bar key={i} className={clsx('h-3', i === rows - 1 ? 'w-2/5' : i % 3 === 1 ? 'w-11/12' : 'w-full')} />
          ))}
        </div>
      )}

      {/* Conversation: alternating asked/answered turns. */}
      {variant === 'chat' && (
        <div className="space-y-5 w-full">
          {lines.map((_, i) => {
            const mine = i % 2 === 1;
            return (
              <div key={i} className={clsx('flex gap-3', mine ? 'justify-end' : 'justify-start')}>
                {!mine && <div className="h-7 w-7 rounded-full bg-surface-sunken flex-shrink-0" />}
                <div className={clsx('space-y-2 max-w-[72%]', mine ? 'items-end' : '')}>
                  <Bar className={clsx('h-3', mine ? 'w-40' : 'w-64')} />
                  <Bar className={clsx('h-3', mine ? 'w-28' : 'w-52')} />
                  {!mine && <Bar className="h-3 w-36" />}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Home/analytics: a KPI strip over two content cards. */}
      {variant === 'dashboard' && (
        <div className="w-full space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <Block key={i} className="p-4 space-y-2">
                <Bar className="h-2.5 w-1/2" />
                <Bar className="h-7 w-2/3" />
                <Bar className="h-2.5 w-1/3" />
              </Block>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Block className="lg:col-span-2 p-5 space-y-3">
              <Bar className="h-3 w-32" />
              <Bar className="h-48 w-full rounded-lg" />
            </Block>
            <Block className="p-5 space-y-3">
              <Bar className="h-3 w-24" />
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-7 w-7 rounded-full bg-surface-sunken flex-shrink-0" />
                  <Bar className="h-3 flex-1" />
                </div>
              ))}
            </Block>
          </div>
        </div>
      )}

      {/* Pickable rows: dropdown options, template/playbook pickers, search hits. */}
      {variant === 'option' && (
        <div className="space-y-2 w-full">
          {lines.map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-hairline-soft bg-surface-sunken/70"
            >
              <div className="h-4 w-4 rounded bg-surface-sunken flex-shrink-0" />
              <div className="flex-1 space-y-1.5">
                <Bar className="h-3 w-1/3" />
                <Bar className="h-2.5 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Schema-driven form: label + field pairs */}
      {variant === 'form' && (
        /* `max-w-3xl mx-auto` mirrors `FormShell`; the gutter is the shared
           inset, so it matches FormShell's body padding rather than its own. */
        <div className="space-y-5 w-full max-w-3xl mx-auto">
          {lines.map((_, i) => (
            <div key={i} className="space-y-2">
              <Bar className="h-2.5 w-28" />
              <FieldBar className="h-9 w-full" />
            </div>
          ))}
          <div className="flex justify-end gap-3 pt-2">
            <FieldBar className="h-9 w-24" />
            <FieldBar className="h-9 w-28" />
          </div>
        </div>
      )}

      {variant === 'kpi' && (
        columns && columns > 1 ? (
          /* The joined strip: one card, N divided cells - the silhouette of a
             `GlassCard padding="none"` KPI band. */
          <Block className="overflow-hidden shadow-sm">
            <div
              className={clsx(
                'grid grid-cols-2 divide-x divide-y divide-hairline-soft lg:divide-y-0',
                KPI_STRIP_COLS[columns] || 'lg:grid-cols-4'
              )}
            >
              {Array.from({ length: columns }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-4">
                  <div className="h-5 w-5 flex-shrink-0 rounded bg-surface-sunken" />
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <Bar className="h-4 w-1/2" />
                    <Bar className="h-2.5 w-2/3" />
                    <Bar className="h-2 w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          </Block>
        ) : (
          <div className="border border-hairline rounded-lg p-4 space-y-2">
            <Bar className="h-3 w-1/2" />
            <Bar className="h-7 w-2/3" />
            <Bar className="h-2.5 w-1/3" />
          </div>
        )
      )}
    </div>
  );
};

export default Skeleton;
